import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import config from '../../config';
import { useGlobalContext } from '../../GlobalContext';
import { GetById } from '../../service/ForumService';
import { Add, GetByForumId, Delete } from '../../service/ForumReplyService';

export default function BatchForumRelpyList() {
  const {
    isLoading,
    setIsLoading,
    setAppError,
    setAppErrorMessage,
    setAppErrorTitle,
    setAppErrorMode,
    appUser
  } = useGlobalContext();

  const { forumId, batchId } = useParams();
  const userId = appUser?._id || '';
  const [forum, setForum] = useState({});
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('');

  const itemsPerPage = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchForum = async () => {
    try {
      setIsLoading(true);
      const response = await GetById(forumId, 1, 1000);
      setForum(response.forum);
      setStatus(response.forum.status);
    } catch (error) {
      setAppError(true);
      setAppErrorTitle("Error");
      setAppErrorMessage("Failed to load data");
      setAppErrorMode("error");
    } finally {
      setIsLoading(false);
    }
  };

  //fetch forum replies by forum id
  const [forumReplies, setForumReplies] = useState([]);
  const fetchForumReplies = async (page = 1) => {
    try {
      setIsLoading(true);
      const response = await GetByForumId(forumId, page, itemsPerPage);
      setForumReplies(response.forumReplies);
      setCurrentPage(response.currentPage);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalReplies);
    } catch (error) {
      setAppError(true);
      setAppErrorTitle("Error");
      setAppErrorMessage("Failed to load forum replies");
      setAppErrorMode("error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchForumReplies(currentPage);
  }, []);


  useEffect(() => {
    fetchForum();
  }, []);

  const createdAt = new Date(forum.createdAt);
  const postedDate = `${String(createdAt.getDate()).padStart(2, '0')}-${String(createdAt.getMonth() + 1).padStart(2, '0')}-${createdAt.getFullYear()}`;

  const saveForum = async (event) => {
    event.preventDefault();

    setIsLoading(true);

    try {
      let response = null;
      response = await Add(forumId, userId, description);
      if (response.status === 200) {
        setAppError(true);
        setAppErrorTitle("Action Response");
        setAppErrorMessage(response.message || "Lesson planner Successfully Updated");
        setAppErrorMode("success");
        window.location.href = `/batch/forum-reply/manage/${forumId}/${batchId}`;
      } else {
        setAppError(true);
        setAppErrorTitle("Error");
        setAppErrorMessage(response.message || "Action failed. Please try again.");
        setAppErrorMode("error");
      }
    } catch (error) {
      setAppError(true);
      setAppErrorTitle("Error");
      setAppErrorMessage("Something went wrong. Please try again.");
      setAppErrorMode("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm("Are you sure you want to remove this message?");
    if (confirmed) {
      const result = await Delete(id);
      if (result.status === 200) {
        setAppError(true);
        setAppErrorTitle("Success");
        setAppErrorMessage(result.message || "Student removed successfully");
        setAppErrorMode("success");
        window.location.href = `/batch/forum-reply/manage/${forumId}/${batchId}`;
      } else {
        setAppError(true);
        setAppErrorTitle("Error");
        setAppErrorMessage(result.message || "Failed to delete student");
        setAppErrorMode("error");
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Forum Reply Details | {config.appName}</title>
      </Helmet>

      <div className='container'>
        <div className='page'>
          <div className='page-heading'>
            <h1>Forum Reply Details</h1>
            <span>
              <Link to="/"> Dashboard </Link> /{' '}
              <Link to={`/batch/forum/manage/list/${batchId}`}> Forum List </Link>
              / Forum Reply Details
            </span>
          </div>

          <div className='page-content'>
            <div className="portal">
              <div className='portal-body'>
                <div className="mb-2">
                  <div className="card-body d-flex align-items-start flex-column flex-md-row">
                    <div className="trainer-avatar me-3 mb-3 mb-md-0">
                      <img
                        src={
                          forum.userId?.image
                            ? `${config.imageBasePath}/users/${forum.userId._id}.${forum.userId.image}`
                            : `/${config.imageBasePath}/default/defaultUser.png`
                        }
                        alt={forum.userId?.username}
                        className='img-fluid image-xs avatar-img'
                      />
                    </div>

                    <div className="flex-grow-1 w-100">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start">
                        <div className="d-flex flex-column">
                          <h5 className="mb-1 text-dark">{forum.topic}</h5>
                          <p className="text-muted mb-2">{forum.description}</p>
                          <div className="small text-secondary">
                            Posted by{' '}
                            <strong className='text-danger'>
                              {forum.userId?.username || 'Unknown'}
                            </strong>{' '}
                            on{' '}
                            <span className='text-danger'>{postedDate}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex-grow-1 w-100 mt-2">
                        <div className="d-flex flex-wrap align-items-center mt-3 mt-md-0">
                          <div className="me-3 d-flex align-items-center text-muted">
                            <i className="fa fa-eye me-1"></i> {forum.views || 0}
                          </div>
                          <div className="me-3 d-flex align-items-center text-muted">
                            <i className="fa fa-comments me-1"></i> {forum.counts || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {status !== 'closed' && (
            <div className='row mt-3'>
              <div className='col-lg-12 col-md-12 col-xs-12'>
                <div className='batch-details'>
                  <form onSubmit={saveForum} encType='multipart/form-data'>
                    <div className='row'>
                      <div className='col-lg-12 col-md-12 col-sm-12 col-12'>
                        <div className="mb-3">
                          <label className="form-label">Reply</label>
                          <textarea className='form-control' value={description} rows={5} onChange={(e) => setDescription(e.target.value)} />
                        </div>
                      </div>
                      <div className='col-12 text-end'>
                        <div className="mb-3">
                          <button type='submit' className='btn btn-success-app btn-md'>
                            <i className="ri-check-fill"></i> Send
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
          <br></br>
          {/* Forum Replies Section */}
          <div className="row">
            <div className="col-lg-12 col-md-12 col-xs-12">
              <div className="card mb-4">
                <div className="card-header bg-light">
                  <h5 className="mb-0">Replies ({totalItems})</h5>
                </div>
                <div className="card-body p-3">

                  {forumReplies.length === 0 ? (
                    <div className="text-muted text-center">No replies yet.</div>
                  ) : (
                    forumReplies.map((reply, index) => {
                      const createdAt = new Date(reply.createdAt);

                      const hours = createdAt.getHours();
                      const minutes = String(createdAt.getMinutes()).padStart(2, '0');
                      const ampm = hours >= 12 ? 'PM' : 'AM';
                      const formattedHours = String(hours % 12 || 12).padStart(2, '0'); // 12-hour format

                      const postedReplyDate = `${String(createdAt.getDate()).padStart(2, '0')}-${String(createdAt.getMonth() + 1).padStart(2, '0')}-${createdAt.getFullYear()} ${formattedHours}:${minutes}${ampm}`;

                      return (
                        <div key={index} className="d-flex mb-4 border-bottom pb-3">
                          <div className="me-3">
                            <img
                              src={
                                reply.userId?.image
                                  ? `${config.imageBasePath}/users/${reply.userId._id}.${reply.userId.image}`
                                  : `/${config.imageBasePath}/default/defaultUser.png`
                              }
                              alt="user"
                              className="rounded-circle"
                              style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                            />
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-center">
                              <strong className="text-dark">
                                {reply.userId?.username || 'Unknown'}
                              </strong>

                              <small className="text-muted">
                                <i className="fa-solid fa-calendar-days me-1"></i>{postedReplyDate}
                              </small>
                            </div>
                            <p className="mt-1 mb-0 text-secondary" style={{ whiteSpace: 'pre-wrap' }}>
                              {reply.description}
                            </p>
                            {reply.userId?._id === appUser?._id && (
                              <div className="d-flex justify-content-end">
                                <button
                                  className='btn btn-sm btn-danger mt-2 px-3 py-1 rounded-pill shadow-sm d-flex align-items-center gap-1'
                                  onClick={() => handleDelete(reply._id)}
                                >
                                  <i className="fa fa-trash"></i> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}

                  {/* Pagination */}
                  {totalItems > itemsPerPage && (
                    <div className="pagination mt-3 d-flex justify-content-center">
                      <span className="align-self-center me-3">
                        {currentPage > 1 && (
                          <button
                            className="btn btn-outline-primary me-2"
                            onClick={() => fetchForumReplies(currentPage - 1)}
                          >
                            Previous
                          </button>
                        )}
                        Page {currentPage} of {totalPages}
                        {currentPage < totalPages && (
                          <button
                            className="btn btn-outline-primary ms-2"
                            onClick={() => fetchForumReplies(currentPage + 1)}
                          >
                            Next
                          </button>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
